import { Flex, Spin } from 'antd'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Cluster, CorrelationStrand, Strand } from 'src/types/type'

import { Button } from '@suntex.fim/ui'

interface CorrelationDetailsProps {
  selectedCorrelation: Cluster
  getModuleContent: (moduleSlug: string) => JSX.Element
}

// Function to combine strands without mutating the original array
function combineStrands(strands: Strand[]): CorrelationStrand[] {
  // Create an empty object to hold the combined strands
  const combined: { [key: string]: CorrelationStrand } = {}

  // Iterate through the strands array
  strands.forEach((item) => {
    const { strand, code, name, fimModules } = item
    // If the strand already exists in the combined object, merge the properties
    if (strand) {
      if (combined[strand] && typeof fimModules === 'string') {
        combined[strand].code.push(String(code))
        combined[strand].name.push(name)
        combined[strand].fimModules.push(fimModules)
      } else {
        // If the strand doesn't exist, create a new entry in the combined object
        combined[strand] = {
          strand,
          code: [String(code)],
          name: [name],
          fimModules: typeof fimModules === 'string' ? [fimModules] : []
        }
      }
    }
  })
  // Convert the combined object back into an array of objects
  return Object.values(combined).map(({ strand, code, name, fimModules }) => ({
    strand,
    code,
    name,
    fimModules
  }))
}
// Transform the selectedCorrelation object into the desired format
function transformObject(selectedCorrelation: any) {
  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    name: selectedCorrelation.name,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    strands: selectedCorrelation.strands.map((strand: any) => ({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      strand: strand.strand,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      codesAndNames: strand.code.map((code: any, index: number) => ({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        code,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        name: strand.name[index],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        fimModules: strand.fimModules[index]
      }))
    }))
  }
}
const CorrelationDetails = memo(
  ({ selectedCorrelation, getModuleContent }: CorrelationDetailsProps) => {
    const combinedStrands = useMemo(
      () => combineStrands([...selectedCorrelation.strands]),
      [selectedCorrelation.strands]
    )
    const convertedSelectedCorrelation = useMemo(
      () =>
        transformObject({ ...selectedCorrelation, strands: combinedStrands }),
      [selectedCorrelation, combinedStrands]
    )
    const [isExpanded, setIsExpanded] = useState<boolean>(false)
    const [expandedCode, setExpandedCode] = useState<string>('')
    const [truncatedTexts, setTruncatedTexts] = useState<Set<string>>(new Set())
    const textRefs = useRef<{ [key: string]: HTMLParagraphElement | null }>({})

    // Check if text is actually truncated by comparing scroll height with client height
    const checkTextTruncation = useCallback((code: string) => {
      const element = textRefs.current[code]
      if (element) {
        const isTextTruncated = element.scrollHeight > element.clientHeight
        setTruncatedTexts((prev) => {
          const newSet = new Set(prev)
          if (isTextTruncated) {
            newSet.add(code)
          } else {
            newSet.delete(code)
          }
          return newSet
        })
      }
    }, []) // No dependencies needed since we're using refs and setState callbacks

    useEffect(() => {
      // Check truncation for all text elements after render
      const checkAllTruncations = () => {
        Object.keys(textRefs.current).forEach((code) => {
          checkTextTruncation(code)
        })
      }

      // Use a timeout to ensure the DOM is fully rendered
      const timeoutId = setTimeout(checkAllTruncations, 0)

      return () => clearTimeout(timeoutId)
    }, [selectedCorrelation]) // Only run when selectedCorrelation changes

    const toggleExpansion = useCallback(
      (code: string) => {
        if (code === expandedCode) {
          setIsExpanded(!isExpanded)
        } else {
          setExpandedCode(code)
          setIsExpanded(true)
        }
      },
      [expandedCode, isExpanded]
    )
    return (
      <div className="sub-correlation-card">
        <div className="correlation-card__heading fw-bold leading-1">
          <h5 className="fw-bold">{convertedSelectedCorrelation.name}</h5>
        </div>
        {convertedSelectedCorrelation.strands.length > 0 ? (
          convertedSelectedCorrelation.strands.map(
            ({ strand, codesAndNames }: any) => (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              <div className="correlation-card__content" key={strand}>
                <h5 className="paragraph-card-item__title fw-bold leading-1">
                  {strand}
                </h5>
                {codesAndNames.map(({ code, name, fimModules }: any) => (
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  <div className="paragraph-card-item__content" key={code}>
                    <Flex gap="10px" vertical className="content-item">
                      <p className="fw-bold text-primary">{code}</p>
                    </Flex>
                    <div className="paragraph-card-item__content">
                      <Flex gap="10px" vertical className="content-item">
                        <ul>
                          {fimModules?.length > 0 &&
                            fimModules
                              .split('\n')
                              .map((moduleSlug: string) => (
                                <li key={moduleSlug}>
                                  {getModuleContent(moduleSlug)}
                                </li>
                              ))}
                        </ul>
                        <Flex vertical align="flex-start">
                          <p
                            ref={(el) => {
                              textRefs.current[code] = el
                              // Check truncation after ref is set
                              if (el) {
                                setTimeout(() => checkTextTruncation(code), 0)
                              }
                            }}
                            className={`clamp-text ${
                              isExpanded && expandedCode === code
                                ? 'showmore-text'
                                : ''
                            }`}
                          >
                            {name}
                          </p>
                          {truncatedTexts.has(code) && (
                            <Button
                              intent="link"
                              onClick={() => toggleExpansion(code)}
                              className="toggle-button"
                            >
                              {isExpanded && expandedCode === code
                                ? 'Read Less'
                                : 'Read More..'}
                            </Button>
                          )}
                        </Flex>
                      </Flex>
                    </div>
                  </div>
                ))}
              </div>
            )
          )
        ) : (
          <Spin />
        )}
      </div>
    )
  }
)

// Display name for better debugging
CorrelationDetails.displayName = 'CorrelationDetails'

export default CorrelationDetails
